import { Injectable } from '@angular/core';
import CryptoJS from 'crypto-js';
import { Constants } from '../app-level-constants';
import { NgZone } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  constants = Constants;
  private readonly key = 'this.constants.SECRETKEY';
  private readonly iv = 'this.constants.IV';

  constructor(private readonly ngZone: NgZone) {}

  encryptObject(obj: unknown): string {
    const data = JSON.stringify(obj);
    return CryptoJS.AES.encrypt(data, this.key).toString();
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = this.safeAtob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private safeAtob(input: string): string {
    let clean = input.replace(/\s/g, '');

    while (clean.length % 4 !== 0) {
      clean += '=';
    }

    return atob(clean);
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const binary = Array.from(bytes)
      .map(b => String.fromCharCode(b))
      .join('');
    return window.btoa(binary);
  }

  async getKey(): Promise<CryptoKey> {
    const rawKey = this.base64ToArrayBuffer(this.key);
    return crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  }

  async encrypt(data: unknown): Promise<string> {
    const key = await this.getKey();
    const iv = new Uint8Array(this.base64ToArrayBuffer(this.iv));
    const encoded = new TextEncoder().encode(JSON.stringify(data));
    const cipherBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
    return this.arrayBufferToBase64(cipherBuffer);
  }

  // async decrypt(cipherBase64: string): Promise<string> {
  //   const key = await this.getKey();
  //   const iv = new Uint8Array(this.base64ToArrayBuffer(this.iv));
  //   const cipherBuffer = this.base64ToArrayBuffer(cipherBase64);
  //   const decryptedBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipherBuffer);
  //   const decoded = new TextDecoder().decode(decryptedBuffer);
  //   return JSON.parse(decoded);
  // }

  async decrypt(cipherBase64: string): Promise<string> {
    return this.ngZone.runOutsideAngular(async () => {
      const key = await this.getKey();
      const iv = new Uint8Array(this.base64ToArrayBuffer(this.iv));
      const cipherBuffer = this.base64ToArrayBuffer(cipherBase64);
      const decryptedBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipherBuffer);
      const decoded = new TextDecoder().decode(decryptedBuffer);
      return this.ngZone.run(() => JSON.parse(decoded));
    });
  }

  async encryptWithPublicKey(publicKeyPem: string, data: object): Promise<string> {
    const key = await this.importPublicKey(publicKeyPem);
    const encoded = new TextEncoder().encode(JSON.stringify(data));
    const encrypted = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, encoded);
    return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  }

  async encryptionMFA(data: object): Promise<string> {
    const publicKey = 'this.constants.PUBLIC_KEY';
    const key = await this.importPublicKey(publicKey);
    const encoded = new TextEncoder().encode(JSON.stringify(data));

    const encrypted = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, key, encoded);

    return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  }

  async decryptWithPrivateKey<T>(privateKeyPem: string, base64Cipher: string): Promise<T> {
    const key = await this.importPrivateKey(privateKeyPem);
    const binary = this.safeAtob(base64Cipher);
    const encryptedBytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      encryptedBytes[i] = binary.charCodeAt(i);
    }

    const decrypted = await window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, key, encryptedBytes.buffer);

    const decoded = new TextDecoder().decode(decrypted);
    return JSON.parse(decoded) as T;
  }

  private async importPublicKey(pem: string): Promise<CryptoKey> {
    const binaryDer = this.pemToArrayBuffer(pem);
    return crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['encrypt']
    );
  }

  private async importPrivateKey(pem: string): Promise<CryptoKey> {
    const binaryDer = this.pemToArrayBuffer(pem);
    return crypto.subtle.importKey(
      'pkcs8',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['decrypt']
    );
  }

  private pemToArrayBuffer(pem: string): ArrayBuffer {
    const b64 = pem.replace(/-----.*?-----/g, '').replace(/\s/g, '');
    const binary = this.safeAtob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  async decryptWithPrivateKey2(encryptedBase64: string, privateKeyPem: string) {
    function pemToArrayBuffer(pem: string) {
      const b64 = pem
        .replace(/-----BEGIN PRIVATE KEY-----/, '')
        .replace(/-----END PRIVATE KEY-----/, '')
        .replace(/\s+/g, '');
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes.buffer;
    }

    const privateKey = await window.crypto.subtle.importKey(
      'pkcs8',
      pemToArrayBuffer(privateKeyPem),
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['decrypt']
    );

    const encryptedBytes = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));

    const decryptedBuffer = await window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, encryptedBytes);

    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  }

  async decryptMessage(ciphertextB64: string): Promise<string> {
    const privateKey = await this.importPrivateKeyLogin('this.constants.PRIVATE_KEY_LOGIN');

    const ciphertext = Uint8Array.from(atob(ciphertextB64), c => c.charCodeAt(0));

    const decryptedBuffer = await crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, ciphertext);

    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  }
  private async importPrivateKeyLogin(pem: string): Promise<CryptoKey> {
    const pemBody = pem
      .replace(/-----BEGIN PRIVATE KEY-----/, '')
      .replace(/-----END PRIVATE KEY-----/, '')
      .replace(/\s+/g, '');
    const binaryDer = Uint8Array.from(atob(pemBody), c => c.charCodeAt(0));

    return crypto.subtle.importKey(
      'pkcs8',
      binaryDer.buffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['decrypt']
    );
  }
}

